/**
 * 中継ストリームの行フォーマット。
 *
 *   `<端末ID 8バイト> <経過ms 8桁> <センテンス>\r\n`
 *
 * 仕様は docs/02。原典の送信側は `GoalView.cpp:1218`、受信側は `GoalView.cpp:1389`。
 */

import { sentenceType } from './nmea.ts';
import type { GvLine } from './types.ts';

export const TID_LENGTH = 8;
export const ELAPSED_DIGITS = 8;
export const ELAPSED_WRAP = 100000000;
/** 8 + 1 + 8 + 1。原典もこの固定オフセットでセンテンスを切り出す */
export const SENTENCE_OFFSET = 18;

/** 端末IDを8バイト固定（右空白詰め）にする */
export function padTerminalId(tid: string): string {
  return tid.slice(0, TID_LENGTH).padEnd(TID_LENGTH, ' ');
}

/**
 * `parseLine`が返す`terminalId`（1文字=1バイトのまま、バイト保存で読んだ
 * 前提の文字列。原典の送出側は日本語の端末名をShift-JISで送ってくることが
 * あり、そのままでは文字化けする）を、表示用に読める文字列へ直す。
 *
 * 純ASCIIの端末ID（"ALmi"等）はShift-JISでも同じ結果になるので、
 * 常にこの関数を通しても既存の見た目に影響しない。
 */
export function decodeTerminalIdLabel(terminalId: string): string {
  const bytes = Uint8Array.from(terminalId, (ch) => ch.charCodeAt(0) & 0xff);
  return new TextDecoder('shift_jis').decode(bytes).trim();
}

/** 送信1行を組み立てる */
export function buildLine(terminalId: string, elapsedMs: number, sentence: string): string {
  const t = Math.floor(elapsedMs) % ELAPSED_WRAP;
  const stamp = String(t < 0 ? t + ELAPSED_WRAP : t).padStart(ELAPSED_DIGITS, '0');
  return `${padTerminalId(terminalId)} ${stamp} ${sentence}\r\n`;
}

/** 受信1行を解析する。18バイト未満は原典と同じく破棄する */
export function parseLine(line: string): GvLine | null {
  const text = line.replace(/[\r\n]+$/, '');
  if (text.length < SENTENCE_OFFSET) return null;
  const terminalIdRaw = text.slice(0, TID_LENGTH);
  const elapsed = Number.parseInt(
    text.slice(TID_LENGTH + 1, TID_LENGTH + 1 + ELAPSED_DIGITS),
    10,
  );
  const sentence = text.slice(SENTENCE_OFFSET);
  return {
    terminalIdRaw,
    terminalId: terminalIdRaw.trim(),
    elapsedMs: Number.isFinite(elapsed) ? elapsed : 0,
    sentence,
    type: sentenceType(sentence),
  };
}

/** バイト列／文字列を行に分割する。LF 区切り・CR 除去。末尾の不完全な行は `rest` に残す */
export function splitLines(text: string): { lines: string[]; rest: string } {
  const lines: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 0x0a) {
      const line = text.slice(start, i).replace(/[\r\n]+$/, '');
      if (line.length > 0) lines.push(line);
      start = i + 1;
    }
  }
  return { lines, rest: text.slice(start) };
}

/** 受信データをまとめて解析する。解析できない行は黙って捨てる（原典と同じ） */
export function parseLines(text: string): GvLine[] {
  return splitLines(text)
    .lines.map(parseLine)
    .filter((l): l is GvLine => l !== null);
}
